import React from 'react';
import { Link } from 'react-router-dom';

// Guide interne : comment donner accès à la plateforme (clients, SDR, internes) et quoi répondre
// quand quelqu'un est bloqué. Texte volontairement non technique — c'est la ressource que
// CSM, management et direction consultent avant d'écrire à Charles.

const Section: React.FC<{ id: string; title: string; children: React.ReactNode }> = ({ id, title, children }) => (
  <section id={id} className="bg-panel border border-line rounded-card p-5 mb-4 scroll-mt-4">
    <h2 className="text-[16px] font-bold tracking-tight mb-3">{title}</h2>
    <div className="text-[13.5px] leading-relaxed text-ink space-y-3">{children}</div>
  </section>
);

const Steps: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ol className="list-decimal pl-5 space-y-1.5 marker:text-hook-rose marker:font-bold">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ol>
);

const Note: React.FC<{ children: React.ReactNode; tone?: 'ok' | 'warn' }> = ({ children, tone = 'ok' }) => (
  <div className={`text-[12.5px] rounded-lg px-3 py-2 border ${tone === 'ok' ? 'bg-st-ok/10 border-st-ok/20' : 'bg-hook-rose/10 border-hook-rose/20'}`}>{children}</div>
);

const AidePage: React.FC = () => (
  <div className="max-w-[820px]">
    <h1 className="text-[21px] font-bold tracking-tight">Guide : accès & onboarding</h1>
    <p className="text-dim text-[13px] mb-4">
      Comment donner accès à la plateforme, et quoi répondre quand quelqu'un n'y arrive pas. Mis à jour le 29/10/2026.
    </p>

    <nav className="flex flex-wrap gap-2 mb-5 text-[12px]">
      {[
        ['client', 'Inviter un client'],
        ['sdr', 'Onboarder un SDR ou un interne'],
        ['parcours', 'Ce que voit la personne invitée'],
        ['bloque', 'Quelqu\'un est bloqué'],
      ].map(([id, l]) => (
        <a key={id} href={`#${id}`} className="px-2.5 py-1 rounded-md border border-line-2 hover:border-ink">{l}</a>
      ))}
    </nav>

    <Section id="client" title="1 · Inviter un utilisateur client">
      <Steps items={[
        <><Link to="/app/utilisateurs-clients" className="text-hook-rose font-semibold hover:underline">Utilisateurs clients</Link> → « Inviter un utilisateur client ».</>,
        <>Email professionnel du client, prénom, nom, téléphone (utile au SDR), et la <b>société</b>. Si la société n'existe pas encore, « Créer » dans la liste déroulante.</>,
        <>« Envoyer l'invitation ». Le mail part dans la minute. C'est tout : pas de mot de passe à transmettre, pas de lien à renvoyer.</>,
      ]} />
      <p>Pour donner accès à une <b>deuxième société</b> au même client, refais une invitation avec la même adresse : aucun mail n'est renvoyé, l'accès à la société est simplement ajouté.</p>
      <Note>Le client voit le portail de sa société : RDV, journées de prospection, campagnes, performance. Il ne voit rien des autres clients.</Note>
    </Section>

    <Section id="sdr" title="2 · Onboarder un nouveau SDR, CSM ou interne">
      <Steps items={[
        <>Créer son adresse <b>prenom.nom@hook-agency.com</b> dans Google Workspace (admin Google), avant tout le reste.</>,
        <><Link to="/app/equipe" className="text-hook-rose font-semibold hover:underline">Équipe & rôles</Link> → « Inviter un interne » : email hook-agency.com, prénom, nom, <b>rôle</b> (SDR, CSM, Interne) et <b>équipe</b> (Cold call, GTM, ou Renfort pour le management).</>,
        <>« Créer le compte ». Il reçoit le mail « Ton accès Hook est prêt ».</>,
        <>Lui dire : sur <b>hook-agency.app</b>, « Continuer avec Google » avec son adresse Hook. Pas de mot de passe à créer. Le code par email marche aussi.</>,
        <>Vérifier sur Équipe & rôles qu'il apparaît dans la bonne équipe : c'est cette affectation qui pilote l'agenda et la capacité vendable.</>,
      ]} />
      <p>Le rôle donne les droits (ce qu'il peut voir et modifier), l'équipe donne le métier (le front qu'il voit en arrivant : journée SDR, GTM, ou vue manager). Seule la Direction peut changer un rôle après coup.</p>
      <p>Quand quelqu'un part : « Marquer comme parti » sur Équipe & rôles. Son compte reste pour l'historique mais sort des équipes et de l'agenda.</p>
      <Note tone="warn">Un SDR invité avec une adresse perso (gmail, etc.) ne pourra pas utiliser Google : il devra passer par le code à chaque fois ou créer un mot de passe. Toujours l'adresse Hook.</Note>
    </Section>

    <Section id="parcours" title="3 · Ce que voit la personne invitée">
      <Steps items={[
        <>Un mail « Ton accès Hook est prêt 👋 » de noreply@hook-agency.app. Pas de bouton, pas de lien : trois étapes écrites en clair. Il reste valable sans limite de temps.</>,
        <>Elle va sur <b>hook-agency.app/login/code</b> et entre son email.</>,
        <>Elle reçoit un second mail « Ton code de connexion Hook » avec 6 chiffres, valables 15 minutes, qu'elle recopie.</>,
        <>Elle est connectée. On lui propose « Continuer avec Google », « Créer un mot de passe », ou « Plus tard ». Les trois marchent : sans choix, elle refera l'étape 2 à chaque connexion.</>,
      ]} />
      <p>« Mot de passe oublié ? » sur la page de connexion passe par le même code, puis propose un nouveau mot de passe.</p>
    </Section>

    <Section id="bloque" title="4 · Quelqu'un est bloqué : quoi répondre">
      <table className="w-full text-[12.5px] border-collapse">
        <thead><tr className="text-left font-mono text-[10px] uppercase tracking-widest text-dim"><th className="py-1.5 pr-3">Ce qu'il dit</th><th className="py-1.5">Réponse</th></tr></thead>
        <tbody className="divide-y divide-line align-top">
          {[
            ['« Je n\'ai pas reçu le mail d\'invitation »', 'Il n\'en a pas besoin. Envoie-le sur hook-agency.app/login/code avec son email.'],
            ['« Je ne reçois pas le code »', 'Spams, puis « Renvoyer un code » (toutes les 60 s). Rien après 5 min : vérifier l\'orthographe de l\'email dans l\'admin.'],
            ['« Cet email n\'a pas d\'accès Hook »', 'L\'email tapé n\'est pas celui invité, ou le compte n\'existe pas. Vérifier dans Utilisateurs clients ou Équipe, inviter si besoin.'],
            ['« J\'ai un vieux mail avec un bouton “Activer mon compte” »', 'Mails envoyés avant le 29/10. Le bouton marche encore, mais le plus simple : même réponse que la première ligne.'],
            ['« J\'ai oublié mon mot de passe »', '« Mot de passe oublié ? » sur la page de connexion → code → nouveau mot de passe.'],
            ['« Mon code dit “incorrect ou expiré” »', 'Plus de 15 min, ou déjà utilisé. « Renvoyer un code ».'],
            ['Un interne : « Google me dit que je n\'ai pas accès »', 'Son compte n\'a pas été créé sur Équipe & rôles, ou avec une autre adresse. Vérifier, inviter.'],
          ].map(([q, a]) => (
            <tr key={q}><td className="py-2 pr-3 font-medium w-[42%]">{q}</td><td className="py-2 text-dim">{a}</td></tr>
          ))}
        </tbody>
      </table>
      <p className="text-[12px] text-faint">Si aucune ligne ne correspond : noter l'heure exacte de l'essai et l'email, et le transmettre à Charles. Les journaux d'authentification permettent de voir ce qui s'est passé à la seconde près.</p>
    </Section>
  </div>
);

export default AidePage;
